<template>
  <v-app-bar app color="white" elevation="1">
    <v-app-bar-nav-icon @click="ui.toggleDrawer()" />

    <v-toolbar-items class="ml-2">
      <v-breadcrumbs dense>
        <template v-for="(crumb, idx) in breadcrumbs" :key="idx">
          <v-breadcrumbs-item :to="crumb.to" :disabled="idx === breadcrumbs.length - 1">
            {{ crumb.text }}
          </v-breadcrumbs-item>

          <!-- Insert a slash if this is not the last item -->
          <span v-if="idx < breadcrumbs.length - 1" class="mx-1" style="user-select: none">/</span>
        </template>
      </v-breadcrumbs>
    </v-toolbar-items>

    <v-toolbar-title class="ml-4"></v-toolbar-title>
    <v-spacer />

    <!-- ─── SEARCH BOX (md+ only) ──────────────────────────────────────────── -->
    <v-text-field
      variant="plain"
      v-model="searchQuery"
      placeholder="Search…"
      density="compact"
      hide-details
      clearable
      prepend-inner-icon="mdi-magnify"
      @keyup.enter="onSearch"
      class="d-none d-md-flex mr-4 search-field"
      style="max-width: 180px; font-size: 0.875rem"
    />

    <!-- ─── TODAY’S DATE ──────────────────────────────────────────────────── -->
    <!-- Always visible; small font so it fits on mobile. -->
    <v-tooltip
      v-if="versionNr"
      :text="updateAvailable ? 'Update available — refresh your browser' : 'Software version (this build)'"
    >
      <template #activator="{ props }">
        <a
          v-bind="props"
          href="#"
          class="mr-3 text-caption text-medium-emphasis font-weight-medium text-primary text-decoration-none"
          @click.prevent="ui.openAbout()"
        >
          v{{ versionNr }}
          <v-icon v-if="updateAvailable" size="x-small" color="warning" class="ms-1">mdi-alert-circle</v-icon>
        </a>
      </template>
    </v-tooltip>

    <!-- ─── TODAY'S DATE ──────────────────────────────────────────────────── -->
    <span class="mr-4 text-caption">{{ todayString }}</span>

    <div
      v-if="headerUserName || customerDisplayName"
      class="mr-6 d-none d-md-flex align-center justify-center text-caption"
      style="min-width: 260px;"
    >
      <span v-if="customerDisplayName" class="font-weight-medium text-medium-emphasis">{{ customerLabel }}</span>
      <span v-if="customerDisplayName && headerUserName" class="mx-2 text-disabled">|</span>
      <span v-if="headerUserName" class="font-weight-medium">{{ headerUserName }}</span>
      <v-tooltip
        v-if="sessionLabel"
        location="bottom"
        text="Auth token expires in this time. While online it refreshes silently. If you go offline and the token expires, you will be logged out."
      >
        <template #activator="{ props }">
          <span
            v-bind="props"
            class="ml-2 font-weight-medium"
            :class="sessionColorClass"
          >
            · {{ sessionLabel }}
          </span>
        </template>
      </v-tooltip>
    </div>

    <!-- Documentation library -->
    <v-tooltip text="Documentation">
      <template #activator="{ props }">
        <v-btn v-bind="props" icon size="large" color="primary" @click="openDocumentationLibrary">
          <v-icon>mdi-book-open-variant</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <v-dialog v-model="documentationDialog" :max-width="selectedDocumentation ? 1180 : 820" scrollable>
      <v-card class="documentation-library">
        <v-card-title class="d-flex align-center py-4 px-5">
          <v-btn v-if="selectedDocumentation" icon="mdi-arrow-left" variant="text" class="mr-2" @click="closeDocumentationReader" />
          <v-icon color="primary" class="mr-3">mdi-book-open-page-variant-outline</v-icon>
          <div>
            <div class="text-h6">{{ selectedDocumentation?.title || 'Help and documentation' }}</div>
            <div class="text-caption text-medium-emphasis">{{ selectedDocumentation ? `Guide ${selectedDocumentation.order}` : 'Choose a guide to read it here' }}</div>
          </div>
          <v-spacer />
          <v-btn v-if="selectedDocumentation" prepend-icon="mdi-open-in-new" variant="text" class="mr-2" @click="openDocumentationExternally">Open externally</v-btn>
          <v-btn icon="mdi-close" variant="text" @click="documentationDialog = false" />
        </v-card-title>
        <v-divider />
        <v-card-text v-if="selectedDocumentation" class="pa-0 documentation-reader">
          <div v-if="documentationPdfLoading" class="documentation-reader-loading">
            <v-progress-circular indeterminate color="primary" />
            <span class="text-body-2 text-medium-emphasis mt-3">Loading guide…</span>
          </div>
          <iframe :key="selectedDocumentation.file" :src="selectedDocumentationUrl" :title="selectedDocumentation.title" class="documentation-frame" @load="documentationPdfLoading = false" />
        </v-card-text>
        <v-card-text v-else class="pa-4">
          <v-btn-toggle v-model="documentationAudience" mandatory color="primary" variant="outlined" divided class="mb-4 documentation-audience">
            <v-btn value="portal"><v-icon start>mdi-monitor-dashboard</v-icon>Portal Guides</v-btn>
            <v-btn value="mobile"><v-icon start>mdi-cellphone</v-icon>Mobile Guide</v-btn>
          </v-btn-toggle>
          <v-text-field v-model="documentationSearch" label="Find a guide" prepend-inner-icon="mdi-magnify" variant="outlined" density="compact" clearable hide-details class="mb-4" />
          <div v-if="documentationLoading" class="d-flex justify-center pa-8">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <v-alert v-else-if="documentationError" type="warning" variant="tonal">{{ documentationError }}</v-alert>
          <v-alert v-else-if="!groupedDocumentation.length" type="info" variant="tonal">No guides match your search.</v-alert>
          <div v-else class="documentation-groups">
            <section v-for="group in groupedDocumentation" :key="group.name" class="documentation-group">
              <div class="text-overline text-medium-emphasis mb-1">{{ group.name }}</div>
              <div class="documentation-grid">
                <button v-for="doc in group.documents" :key="doc.file" type="button" class="documentation-item" @click="openDocumentation(doc)">
                  <v-icon color="primary" size="22">{{ doc.portalIcon || doc.icon }}</v-icon>
                  <span class="documentation-number">{{ doc.order }}</span>
                  <span class="documentation-title">{{ doc.title }}</span>
                  <v-icon color="medium-emphasis" size="16">mdi-open-in-new</v-icon>
                </button>
              </div>
            </section>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Intro.js Guided Tour Button -->
    <v-tooltip text="Interactive Guided Tour">
      <template v-slot:activator="{ props }">
        <v-btn v-bind="props" icon size="large" color="secondary" @click="startIntroTour">
          <v-icon>mdi-crosshairs-gps</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <!-- System Admin: visible when isSystemAdmin or email is info@waywyser.com (fallback) -->
    <v-btn
      v-if="auth.isSystemAdmin || isAdminEmail"
      color="primary"
      variant="tonal"
      class="mr-2"
      to="/admin/customers"
    >
      <v-icon start>mdi-shield-account</v-icon>
      System Admin
    </v-btn>

    <!-- ─── USER AVATAR MENU WITH TOOLTIP ───────────────────────────────────── -->
    <v-menu offset-y>
      <template #activator="{ props: menuProps }">
        <v-tooltip :text="userDisplayName">
          <template #activator="{ props: tooltipProps }">
            <v-avatar
              class="cursor-pointer mr-2"
              size="36"
              v-bind="{ ...menuProps, ...tooltipProps }"
            >
              <v-img :src="getUserIconUrl(selectedIcon)" />
            </v-avatar>
          </template>
        </v-tooltip>
      </template>

      <v-list>
        <v-list-item @click="avatarDialog = true">
          <template #prepend>
            <v-icon>mdi-account-circle</v-icon>
          </template>
          <v-list-item-title>Choose Avatar</v-list-item-title>
        </v-list-item>

        <v-list-item :href="changePasswordUrl" target="_self" rel="noopener">
          <template #prepend>
            <v-icon>mdi-key</v-icon>
          </template>
          <v-list-item-title>Change Password</v-list-item-title>
        </v-list-item>

        <v-divider />

        <!-- Dark Mode Toggle -->
        <v-list-item @click="toggleTheme">
          <template #prepend>
            <v-tooltip text="Toggle Dark/Light Theme">
              <template #activator="{ props }">
                <v-icon v-bind="props" :color="isDark ? 'yellow' : 'blue-darken-2'">
                  {{ isDark ? "mdi-brightness-7" : "mdi-brightness-4" }}
                </v-icon>
              </template>
            </v-tooltip>
          </template>
          <v-list-item-title>Theme</v-list-item-title>
        </v-list-item>

        <!-- Logout inside avatar menu -->
        <v-list-item @click="onLogout">
          <template #prepend>
            <v-icon color="error">mdi-logout</v-icon>
          </template>
          <v-list-item-title>Logout</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- ─── AVATAR DIALOG ──────────────────────────────────────────────── -->
    <v-dialog v-model="avatarDialog" max-width="500">
      <v-card>
        <v-card-title>Choose Your Avatar</v-card-title>
        <v-card-text>
          <v-row>
            <v-col v-for="iconId in iconOptions" :key="iconId" cols="2" class="text-center">
              <v-avatar
                size="48"
                class="cursor-pointer"
                :style="{
                  border: selectedIcon === iconId ? '3px solid #1976d2' : '1px solid #e0e0e0',
                }"
                @click="selectedIcon = iconId"
              >
                <v-img :src="getUserIconUrl(iconId)" />
              </v-avatar>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="avatarDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="saveIcon">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── ABOUT POPUP ────────────────────────────────────────────────── -->
    <AboutDialog />
  </v-app-bar>
</template>

<script setup>
/* global __APP_VERSION__ */
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useUIStore } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";
import { userManager } from "@/auth/oidc";
import { useTheme } from "vuetify";
import { useDialogStore } from "@/stores/dialog";
import axios from "axios";
import introJs from "intro.js";
import "intro.js/introjs.css";
import { getLatestVersion } from "@/api/softwareVersion";
import AboutDialog from "@/components/AboutDialog.vue";
import documentationManifest from "../../../WW_Vue.Server/wwwroot/Docs/documents.json";

const ui = useUIStore();
const auth = useAuthStore();
const route = useRoute();
const dialog = useDialogStore();
const avatarDialog = ref(false);
const selectedIcon = ref(auth.user?.profile?.icon_id || 1);
const iconOptions = Array.from({ length: 50 }, (_, i) => i + 1);

// Show the TRUE running build (compiled in), not the DB's published number — otherwise
// the header disagrees with the About screen. See About.vue for the have/should comparison.
const versionNr = ref(
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : import.meta.env.VITE_VERSION || ""
);

// Surface an "update available" hint when the published version is ahead of this build.
const updateAvailable = ref(false);

onMounted(async () => {
  try {
    const v = await getLatestVersion();
    const published = v?.versionNr;
    if (published && published !== versionNr.value) {
      const cmp = compareVersions(versionNr.value, published);
      updateAvailable.value = cmp != null && cmp < 0;
    }
  } catch {
    /* keep the build version; update hint stays off */
  }
});

// Numeric-dotted compare: -1/0/1, or null if not comparable.
function compareVersions(a, b) {
  if (!a || !b) return null;
  const pa = String(a).split(".").map((n) => parseInt(n, 10));
  const pb = String(b).split(".").map((n) => parseInt(n, 10));
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

// OIDC profile.name can be string or array; VTooltip requires string
const userDisplayName = computed(() => {
  const n = auth.user?.profile?.name;
  if (!n) return "User Menu";
  return Array.isArray(n) ? n.join(" ") : String(n);
});

const headerUserName = computed(() => {
  const profile = auth.user?.profile || {};
  const value = profile.name || profile.preferred_username || profile.email || profile.sub || "";
  return Array.isArray(value) ? value.join(" ") : String(value);
});

const customerDisplayName = computed(() => {
  const profile = auth.user?.profile || {};
  const value = auth.authMe?.customerName || profile.customer_name || profile.customerName || "";
  return Array.isArray(value) ? value.join(" ") : String(value);
});

// Customer name with its id appended, e.g. "Acme Ltd (42)". Falls back to just
// the name when no id is resolved.
const customerLabel = computed(() => {
  const name = customerDisplayName.value;
  if (!name) return "";
  const id = auth.customerId;
  return id != null && id !== "" && id !== "null" ? `${name} (${id})` : name;
});

// Session countdown chip. Mirrors the mobile header. Reactive clock —
// 5-min ticks are plenty when the trusted-device token lasts 90 days; the
// chip shows days/hours/minutes, not seconds.
const _now = ref(Date.now());
let _sessionTimer = null;
onMounted(() => {
  _sessionTimer = setInterval(() => { _now.value = Date.now(); }, 5 * 60_000);
});
onUnmounted(() => {
  if (_sessionTimer) {
    clearInterval(_sessionTimer);
    _sessionTimer = null;
  }
});

const sessionMinutesLeft = computed(() => {
  const exp = auth.user?.expires_at;
  if (!exp) return null;
  return Math.round((exp * 1000 - _now.value) / 60000);
});

const sessionLabel = computed(() => {
  const m = sessionMinutesLeft.value;
  if (m == null) return "";
  if (m <= 0) return "Session expired";
  const days = Math.floor(m / (60 * 24));
  if (days >= 1) return `${days}d left`;
  const hours = Math.floor(m / 60);
  if (hours >= 1) {
    const r = m % 60;
    return r ? `${hours}h ${r}m left` : `${hours}h left`;
  }
  return `${m}m left`;
});

// Vuetify text-utility classes. Tuned for a 90-day trusted token: orange
// in the last week, red in the last day. Untrusted (60-min) tokens go red
// straight away — they are inherently short.
const sessionColorClass = computed(() => {
  const m = sessionMinutesLeft.value;
  if (m == null) return "";
  if (m <= 0) return "text-error";
  if (m <= 60 * 24) return "text-error";       // ≤ 24h
  if (m <= 60 * 24 * 7) return "text-warning"; // ≤ 7 days
  return "text-medium-emphasis";
});

// Fallback: show System Admin link when email is info@waywyser.com (in case auth/me fails)
const isAdminEmail = computed(() => {
  const email = auth.user?.profile?.email;
  return email === "info@waywyser.com" || (Array.isArray(email) && email.includes("info@waywyser.com"));
});

const authority = import.meta.env.VITE_AUTHORITY || "https://localhost:7188";
const changePasswordUrl = `${authority}/Identity/Account/Manage/ChangePassword`;

const breadcrumbs = computed(() => {
  return route.matched
    .filter((r) => r.meta?.breadcrumb)
    .map((r) => {
      // Special links for parent routes
      if (r.name === "ZoneParent") {
        return { text: r.meta.breadcrumb, to: { name: "ZoneList" } };
      }
      if (r.name === "FormParent") {
        return { text: r.meta.breadcrumb, to: { name: "FormList" } };
      }

      // Identify which params are valid for this route
      const validParams = Object.keys(route.params).filter((key) => r.path.includes(`:${key}`));

      const params = validParams.length
        ? Object.fromEntries(validParams.map((k) => [k, route.params[k]]))
        : undefined;

      return {
        text: r.meta.breadcrumb,
        to: r.name ? { name: r.name, params } : { path: r.path },
      };
    });
});

// ─── DOCUMENTATION LINKS ──────────────────────────────────────────────────

const legacyDocumentationLinks = [
  {
    title: "1. Introduction",
    icon: "mdi-information-outline",
    href: "https://www.waywyser.com/wp-content/uploads/2025/07/1.-Introduction.pdf",
  },
  {
    title: "2. Getting Started",
    icon: "mdi-play-circle-outline",
    href: "https://www.waywyser.com/wp-content/uploads/2025/07/2.-Getting-Started.pdf",
  },
  {
    title: "3a. Field Operations",
    icon: "mdi-walk",
    href: "https://www.waywyser.com/wp-content/uploads/2025/07/3a.-Field-Operations-with-WayWyser-roles-zones-folders-forms-cs.pdf",
  },
  {
    title: "3b. Attraction Mgmt",
    icon: "mdi-map-marker-radius",
    href: "https://www.waywyser.com/wp-content/uploads/2025/07/3b.-Field-Ops-Attention-Points-and-Visitor-Attraction-Management.pdf",
  },
  {
    title: "3c. Tasks Workflow",
    icon: "mdi-format-list-checks",
    href: "https://www.waywyser.com/wp-content/uploads/2025/07/3c.-Tasks.Managing-Workflow-with-the-Tasks-Module.pdf",
  },
  {
    title: "5. GPS Integration",
    icon: "mdi-crosshairs-gps",
    href: "https://www.waywyser.com/wp-content/uploads/2025/07/5.-Digital-Mapping-and-GPS-Integration.pdf",
  },
  {
    title: "6. Troubleshooting",
    icon: "mdi-lifebuoy",
    href: "https://www.waywyser.com/wp-content/uploads/2025/07/6.-Troubleshooting-and-Support.pdf",
  },
  {
    title: "7. Conclusion",
    icon: "mdi-check-circle-outline",
    href: "https://www.waywyser.com/wp-content/uploads/2025/07/7.-Conclusion.pdf",
  },
  {
    title: "8. Glossary",
    icon: "mdi-book-outline",
    href: "https://www.waywyser.com/wp-content/uploads/2025/07/8.-Glossary-of-Terms.pdf",
  },
];
const openLegacyDocumentation = (href) => {
  window.open(href, "_blank");
}; // ✅ Make sure this closing brace and semicolon exist

// ─── SEARCH BOX STATE & HANDLER ───────────────────────────────────────────
const documentationDialog = ref(false);
const documentationAudience = ref("portal");
const documentationSearch = ref("");
const documentationLinks = ref({
  portal: documentationManifest.portal || [],
  mobile: documentationManifest.mobile || [],
});
const documentationLoading = ref(false);
const documentationError = ref("");
const selectedDocumentation = ref(null);
const documentationPdfLoading = ref(false);
// The help PDFs live in the public `documents` container in Azure Blob storage
// (trailmanagerblobstorage). Serving them straight from blob means they never
// 404 on an IIS republish and don't depend on the API host. Override with
// VITE_DOCS_BASE_URL if the location ever changes.
const docsBaseUrl = (import.meta.env.VITE_DOCS_BASE_URL || "https://trailmanagerblobstorage.blob.core.windows.net/documents")
  .replace(/\/$/, "");

const groupedDocumentation = computed(() => {
  const query = documentationSearch.value.trim().toLowerCase();
  const filtered = (documentationLinks.value[documentationAudience.value] || []).filter((doc) =>
    !query || `${doc.order} ${doc.title} ${doc.group}`.toLowerCase().includes(query)
  );
  const groups = new Map();
  filtered.forEach((doc) => {
    if (!groups.has(doc.group)) groups.set(doc.group, []);
    groups.get(doc.group).push(doc);
  });
  return Array.from(groups, ([name, documents]) => ({ name, documents }));
});

function openDocumentationLibrary() {
  documentationDialog.value = true;
}

function openDocumentation(doc) {
  // Open the PDF in a new browser tab (native viewer) instead of an <iframe>.
  // The docs are served from the API host, which sends `frame-ancestors 'none'`
  // + `X-Frame-Options: DENY` on every response, so cross-origin framing is
  // blocked ("Framing … violates … frame-ancestors 'none'"). A top-level
  // navigation isn't subject to those headers, so this always works — and gives
  // the browser's built-in PDF viewer (download/print) for free.
  const url = documentationUrl(doc);
  if (url) window.open(url, "_blank", "noopener");
}

const selectedDocumentationUrl = computed(() => {
  if (!selectedDocumentation.value) return "";
  return documentationUrl(selectedDocumentation.value);
});

function documentationUrl(doc) {
  const path = doc.file.split("/").map(encodeURIComponent).join("/");
  return `${docsBaseUrl}/${path}`;
}

function closeDocumentationReader() {
  selectedDocumentation.value = null;
  documentationPdfLoading.value = false;
}

function openDocumentationExternally() {
  if (!selectedDocumentation.value) return;
  window.open(documentationUrl(selectedDocumentation.value), "_blank", "noopener");
}

const searchQuery = ref("");
function onSearch() {
  if (!searchQuery.value) return;
  // Insert your real search logic here. For example:
  // router.push({ name: 'SearchResults', query: { q: searchQuery.value } });
  // Clear the input (optional)
  // searchQuery.value = "";
}

// ─── TODAY’S DATE FORMATTED ───────────────────────────────────────────────
const todayString = computed(() => {
  const d = new Date();
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
});

// ─── DARK MODE TOGGLE ──────────────────────────────────────────────────
const theme = useTheme();
const isDark = computed(() => theme.global.current.value.dark);

function toggleTheme() {
  theme.global.name.value = theme.global.current.value.dark ? "light" : "dark";
}

// ─── USER PROFILE ACTIONS ──────────────────────────────────────────────
async function onLogout() {
  const result = await dialog.confirm({
    title: "Logout",
    message: "Are you sure you want to logout?",
    variant: "warn",
    buttons: "ok-cancel"
  });
  
  if (result === "ok") {
    try {
      // 1. Call your auth store logout (handles token cleanup, etc.)
      await auth.logout();

      // 2. The auth store should handle the redirect
      // If not, add: router.push('/login')
    } catch (error) {
      console.error("Logout error:", error);
      // Fallback: Force logout
      auth.user = null;
      delete axios.defaults.headers.common.Authorization;
      localStorage.clear(); // Clear any remaining tokens
      window.location.href = "/login";
    }
  }
}

// ─── GET USER ICON URL FUNCTION ──────────────────────────────────────
function getUserIconUrl(id) {
  const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), iconOptions.length)).padStart(
    2,
    "0"
  );
  return `/images/avatars/${key}.svg`;
}

// ─── SAVE ICON FUNCTION ──────────────────────────────────────────────
async function saveIcon() {
  try {
    // Import the setIcon function (you might need to adjust this import)
    const { setIcon } = await import("@/api/account");

    await setIcon(selectedIcon.value);

    // Update the auth store
    if (auth.user?.profile) {
      auth.user.profile.icon_id = selectedIcon.value;
    }

    avatarDialog.value = false;
  } catch (error) {
  }
}

//INTRO TOURS
const showIntroButton = ref(true);

function startIntroTour() {
  showIntroButton.value = false;

  const intro = introJs();
  intro.setOptions({
    steps: [
      {
        element: ".v-navigation-drawer",
        intro: "<h4>🏠 Main Navigation</h4><p>Spotlight on the sidebar menu.</p>",
        position: "right",
      },
      {
        element: 'a[href="/"]',
        intro: "<h4>📊 Overview Dashboard</h4><p>Highlighting the overview link.</p>",
        position: "right",
      },
      {
        element: 'a[href="/zones"]',
        intro: "<h4>🗺️ Zones</h4><p>Spotlight follows each item.</p>",
        position: "right",
      },
      {
        element: '[title="Quick Actions"]',
        intro: "<h4>⚡ Quick Actions</h4><p>Highlighting header shortcuts.</p>",
        position: "bottom",
      },
    ],
    showStepNumbers: true,
    exitOnOverlayClick: false,
    overlayOpacity: 0.8,
    scrollToElement: true,
    nextLabel: "Next →",
    prevLabel: "← Previous",
    skipLabel: "Skip",
    doneLabel: "Finish! 🎉",
  });

  intro.oncomplete(() => (showIntroButton.value = true));
  intro.onexit(() => (showIntroButton.value = true));
  intro.start();
}
</script>

<style scoped>
.v-breadcrumbs {
  font-size: 0.875rem; /* ~14px so it fits nicely in the app-bar */
  margin-top: 0;
  margin-bottom: 0;
}
/* ─── Shrink the overall “field control” to exactly 32px ───────────────── */
.search-field .v-field__control {
  min-height: 32px !important;
  height: 32px !important;
}

/* ─── Vertically center the prepend‐inner icon ───────────────────────────── */
.search-field .v-field__prefix .v-icon {
  margin-top: 0 !important;
  line-height: 32px !important;
}

/* ─── Adjust the <input> so text is centered vertically inside 32px ───── */
.search-field input {
  height: 32px !important;
  line-height: 32px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.text-caption {
  font-size: 0.85rem !important;
}

.documentation-library {
  width: 100%;
  height: min(82vh, 760px);
  max-height: min(82vh, 760px);
}
.documentation-library > :deep(.v-card-text) {
  min-height: 0;
  overflow-y: auto;
}
.documentation-reader {
  position: relative;
  height: min(76vh, 760px);
  min-height: 520px;
  overflow: hidden;
}
.documentation-frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: #f4f5f7;
}
.documentation-reader-loading {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-surface));
}
.documentation-groups {
  display: grid;
  gap: 18px;
}
.documentation-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.documentation-item {
  display: grid;
  grid-template-columns: 24px 34px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  padding: 8px 10px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  text-align: left;
  cursor: pointer;
}
.documentation-item:hover,
.documentation-item:focus-visible {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.06);
  outline: none;
}
.documentation-number {
  color: rgb(var(--v-theme-primary));
  font-size: 0.78rem;
  font-weight: 700;
}
.documentation-title {
  min-width: 0;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.25;
}
@media (max-width: 600px) {
  .documentation-grid {
    grid-template-columns: 1fr;
  }
  .documentation-reader {
    min-height: 65vh;
  }
}
</style>
